import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const ValidationFormVehicule = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const demandeId = queryParams.get('demande_id');
    
    const [formData, setFormData] = useState({
        demande_reparation_id: demandeId || '',
        decision: 'accepte',
        commentaire: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [user, setUser] = useState(null);
    const [demande, setDemande] = useState(null);
    const [demandesEnAttente, setDemandesEnAttente] = useState([]);
    const [selectedDemandePreview, setSelectedDemandePreview] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');

    const token = localStorage.getItem('token');

    const showCustomAlert = (message, type = 'success') => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        
        setTimeout(() => {
            setShowAlert(false);
        }, 5000);
    };

    useEffect(() => {
        const fetchUserAndData = async () => {
            try {
                const userData = localStorage.getItem('user');
                if (userData) {
                    const parsedUser = JSON.parse(userData);
                    setUser(parsedUser);
                    
                    if (!['validateur', 'chef_equipe'].includes(parsedUser.role)) {
                        navigate('/demandes-reparation');
                        return;
                    }
                } else {
                    navigate('/login');
                    return;
                }

                if (demandeId) {
                    await fetchDemandeDetails(demandeId);
                } else {
                    await fetchDemandesEnAttente();
                }
            } catch (err) {
                console.error('Erreur:', err);
                setError('Erreur de chargement des données');
                showCustomAlert('Erreur de chargement des données', 'error');
            }
        };

        fetchUserAndData();
    }, [demandeId, navigate]);

    useEffect(() => {
        if (formData.demande_reparation_id && !demandeId) {
            const selected = demandesEnAttente.find(d => d.id == formData.demande_reparation_id);
            setSelectedDemandePreview(selected);
        }
    }, [formData.demande_reparation_id, demandesEnAttente, demandeId]);

    const fetchDemandeDetails = async (id) => {
        try {
            const response = await axios.get(`${API_URL}/demandes-reparations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDemande(response.data);
            
            if (response.data.statut !== 'en_attente') {
                showCustomAlert('Cette demande a déjà été traitée', 'warning');
                setTimeout(() => navigate(`/demandes-reparation/${id}`), 1500);
                return;
            }
            
            setFormData(prev => ({
                ...prev,
                demande_reparation_id: id
            }));
        } catch (err) {
            showCustomAlert('Erreur lors du chargement de la demande', 'error');
        }
    };

    const fetchDemandesEnAttente = async () => {
        try {
            const response = await axios.get(`${API_URL}/demandes-reparations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const enAttente = Array.isArray(response.data) 
                ? response.data.filter(d => d.statut === 'en_attente')
                : response.data.data?.filter(d => d.statut === 'en_attente') || [];
            setDemandesEnAttente(enAttente);
        } catch (err) {
            setDemandesEnAttente([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDecisionChange = (decision) => {
        setFormData(prev => ({
            ...prev,
            decision,
            commentaire: decision === 'accepte' ? '' : prev.commentaire
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (!formData.demande_reparation_id) {
            showCustomAlert('Veuillez sélectionner une demande', 'error');
            setLoading(false);
            return;
        }

        if (formData.decision === 'refuse' && !formData.commentaire.trim()) {
            showCustomAlert('Un commentaire est obligatoire pour justifier un refus', 'error');
            setLoading(false);
            return;
        }

        try {
            await axios.post(`${API_URL}/validations-reparations`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const decisionText = formData.decision === 'accepte' 
                ? 'validée' 
                : formData.decision === 'modifie'
                ? 'modifiée'
                : 'refusée';
            
            showCustomAlert(`Demande ${decisionText} avec succès`, 'success');
            
            setTimeout(() => {
                if (demandeId) {
                    navigate(`/demandes-reparation/${demandeId}`);
                } else {
                    navigate('/demandes-reparation');
                }
            }, 1500);
            
        } catch (err) {
            const errorMessage = err.response?.data?.message 
                || err.response?.data?.error 
                || err.message 
                || 'Une erreur est survenue lors de la validation';
            showCustomAlert(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (user && !['validateur', 'chef_equipe'].includes(user.role)) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="max-w-md w-full">
                    <div className="bg-white p-8 rounded-lg border text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Accès restreint</h2>
                        <p className="text-gray-600 mb-6">Accès réservé aux validateurs et chefs d'équipe.</p>
                        <Link 
                            to="/demandes-reparation" 
                            className="inline-flex items-center text-blue-600 hover:text-blue-800"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Retour aux demandes
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const currentDemande = demande || selectedDemandePreview;

    return (
        <div className="min-h-screen ">
            {/* Alerte flottante */}
            {showAlert && (
                <div className="fixed top-4 right-4 z-50 max-w-md">
                    <div className={`rounded-lg shadow-lg border p-4 flex items-center justify-between ${
                        alertType === 'success' ? 'bg-green-50 border-green-200' :
                        alertType === 'error' ? 'bg-red-50 border-red-200' :
                        'bg-yellow-50 border-yellow-200'
                    }`}>
                        <div className="flex items-center">
                            <div className={`mr-3 ${
                                alertType === 'success' ? 'text-green-500' :
                                alertType === 'error' ? 'text-red-500' :
                                'text-yellow-500'
                            }`}>
                                {alertType === 'success' ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : alertType === 'error' ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                )}
                            </div>
                            <div>
                                <p className={`font-medium ${
                                    alertType === 'success' ? 'text-green-800' :
                                    alertType === 'error' ? 'text-red-800' :
                                    'text-yellow-800'
                                }`}>
                                    {alertMessage}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAlert(false)}
                            className={`ml-4 ${
                                alertType === 'success' ? 'text-green-500 hover:text-green-700' :
                                alertType === 'error' ? 'text-red-500 hover:text-red-700' :
                                'text-yellow-500 hover:text-yellow-700'
                            }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Validation de demande</h1>
                            <p className="text-gray-600 mt-1">Évaluez et traitez les demandes de réparation</p>
                        </div>
                        <Link
                            to={demandeId ? `/demandes-reparation/${demandeId}` : "/demandes-reparation"}
                            className="inline-flex items-center text-gray-600 hover:text-gray-900"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Retour
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Formulaire principal */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg border p-6">
                            <div className="mb-6">
                                <h2 className="font-semibold text-gray-900 mb-2">Décision de validation</h2>
                                <p className="text-gray-600 text-sm">Sélectionnez une demande et prenez votre décision</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Sélection de la demande */}
                                {!demandeId && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Sélectionner une demande
                                        </label>
                                        <select
                                            name="demande_reparation_id"
                                            value={formData.demande_reparation_id}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Choisir une demande...</option>
                                            {demandesEnAttente.map(demande => (
                                                <option key={demande.id} value={demande.id}>
                                                    #{demande.id} - {demande.vehicule.numero || 'N/A'} - {demande.type_reparation}
                                                </option>
                                            ))}
                                        </select>
                                        {demandesEnAttente.length === 0 && (
                                            <p className="text-sm text-gray-500 mt-2">Aucune demande en attente</p>
                                        )}
                                    </div>
                                )}

                                {/* Décision */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Votre décision
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => handleDecisionChange('accepte')}
                                            className={`p-4 border rounded-lg text-center transition-colors ${
                                                formData.decision === 'accepte'
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-gray-300 hover:'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 mx-auto mb-2 rounded-full flex items-center justify-center ${
                                                formData.decision === 'accepte'
                                                    ? 'bg-green-500'
                                                    : 'bg-gray-200'
                                            }`}>
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <div className="font-medium text-gray-900">Accepter</div>
                                            <div className="text-xs text-gray-500">Valider la demande</div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleDecisionChange('modifie')}
                                            className={`p-4 border rounded-lg text-center transition-colors ${
                                                formData.decision === 'modifie'
                                                    ? 'border-yellow-500 bg-yellow-50'
                                                    : 'border-gray-300 hover:'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 mx-auto mb-2 rounded-full flex items-center justify-center ${
                                                formData.decision === 'modifie'
                                                    ? 'bg-yellow-500'
                                                    : 'bg-gray-200'
                                            }`}>
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </div>
                                            <div className="font-medium text-gray-900">Modifier</div>
                                            <div className="text-xs text-gray-500">Demander ajustements</div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleDecisionChange('refuse')}
                                            className={`p-4 border rounded-lg text-center transition-colors ${
                                                formData.decision === 'refuse'
                                                    ? 'border-red-500 bg-red-50'
                                                    : 'border-gray-300 hover:'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 mx-auto mb-2 rounded-full flex items-center justify-center ${
                                                formData.decision === 'refuse'
                                                    ? 'bg-red-500'
                                                    : 'bg-gray-200'
                                            }`}>
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </div>
                                            <div className="font-medium text-gray-900">Refuser</div>
                                            <div className="text-xs text-gray-500">Rejeter la demande</div>
                                        </button>
                                    </div>
                                </div>

                                {/* Commentaire */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Commentaire {formData.decision === 'refuse' && <span className="text-red-500">*</span>}
                                    </label>
                                    <textarea
                                        name="commentaire"
                                        value={formData.commentaire}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder={
                                            formData.decision === 'refuse'
                                                ? "Justifiez votre décision..."
                                                : "Commentaire optionnel..."
                                        }
                                        required={formData.decision === 'refuse'}
                                    />
                                    <div className="text-sm text-gray-500 mt-2">
                                        {formData.decision === 'refuse'
                                            ? "Ce champ est obligatoire pour le refus"
                                            : "Recommandé pour toute décision"
                                        }
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="pt-6 border-t">
                                    <div className="flex items-center justify-between">
                                        <Link
                                            to={demandeId ? `/demandes-reparation/${demandeId}` : "/demandes-reparation"}
                                            className="px-4 py-2 border rounded text-gray-700 hover:"
                                        >
                                            Annuler
                                        </Link>
                                        
                                        <button
                                            type="submit"
                                            disabled={loading || !formData.demande_reparation_id}
                                            className={`px-6 py-2 rounded font-medium ${
                                                loading || !formData.demande_reparation_id
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : formData.decision === 'accepte'
                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                    : formData.decision === 'modifie'
                                                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                                                    : 'bg-red-600 text-white hover:bg-red-700'
                                            }`}
                                        >
                                            {loading ? (
                                                <div className="flex items-center">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Traitement...
                                                </div>
                                            ) : (
                                                formData.decision === 'accepte'
                                                    ? 'Valider la demande'
                                                    : formData.decision === 'modifie'
                                                    ? 'Demander modifications'
                                                    : 'Refuser la demande'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Note informative */}
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex">
                                <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div>
                                    <p className="text-sm font-medium text-blue-800">Validation en cours</p>
                                    <p className="text-sm text-blue-700 mt-1">
                                        Vous êtes sur le point de {formData.decision === 'accepte' ? 'valider' : formData.decision === 'modifie' ? 'modifier' : 'refuser'} 
                                        la demande. Cette action sera enregistrée dans l'historique.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Détails de la demande */}
                        <div className="bg-white rounded-lg border p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">Détails de la demande</h3>
                                <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">En attente</span>
                            </div>
                            
                            {currentDemande ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">N° demande</p>
                                            <p className="font-medium">#{currentDemande.id}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Type</p>
                                            <p className="font-medium">{currentDemande.type_reparation}</p>
                                        </div>
                                    </div>

                                    <div className="border-t pt-4">
                                        <p className="text-sm text-gray-500 mb-2">Véhicule</p>
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="font-medium">{currentDemande.vehicule.numero || 'N/A'}</p>
                                                <p className="text-sm text-gray-500">
                                                    {currentDemande.vehicule?.marque} {currentDemande.vehicule?.modele}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 border-t pt-4">
                                        <div>
                                            <p className="text-sm text-gray-500">Kilométrage</p>
                                            <p className="font-medium">{currentDemande.km_actuel?.toLocaleString() || '0'} km</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Date</p>
                                            <p className="font-medium">
                                                {new Date(currentDemande.created_at).toLocaleDateString('fr-FR')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="border-t pt-4">
                                        <p className="text-sm text-gray-500 mb-2">Demandeur</p>
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="font-medium">{currentDemande.demandeur?.name || 'Utilisateur'}</p>
                                                <p className="text-sm text-gray-500">{currentDemande.demandeur?.role}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-t pt-4">
                                        <Link
                                            to={`/demandes-reparation/${currentDemande.id}`}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                        >
                                            Voir les détails complets →
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-500">Sélectionnez une demande</p>
                                </div>
                            )}
                        </div>

                        {/* Validateur */}
                        <div className="bg-white rounded-lg border p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Validateur</h3>
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-medium">{user?.name || 'Utilisateur'}</p>
                                    <p className="text-sm text-gray-500 capitalize">{user?.role}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {demandesEnAttente.length} demande(s) en attente
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Guide */}
                        <div className="bg-white rounded-lg border p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Guide de décision</h3>
                            <div className="space-y-3">
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-2"></div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Accepter</p>
                                        <p className="text-xs text-gray-500">La demande répond aux critères</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 mr-2"></div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Modifier</p>
                                        <p className="text-xs text-gray-500">Ajustements nécessaires</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2"></div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Refuser</p>
                                        <p className="text-xs text-gray-500">Ne répond pas aux exigences</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ValidationFormVehicule;